import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import AppContainer from "../../common/app_container";
import Alert, {AlertProps} from "../../common/alert";
import {SubmitHandler, useForm} from "react-hook-form";
import {INDUSTRY_TYPES, VoiceTrainingScriptDTO} from "@src/model/voice_training_script";
import {useVoiceTrainingScriptAPI} from "@src/api/voice_training_script_api";
import {useOrganizationAPI} from "@src/api/organization.api";
import {Organization} from "@src/model/organization";

const VoiceTrainingScriptEdit: React.FC = () => {
    const {id} = useParams<{ id: string }>();
    const navigate = useNavigate();
    const {getScriptById, createScript, updateScript} = useVoiceTrainingScriptAPI();
    const {getAllOrganizations} = useOrganizationAPI();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetching, setIsFetching] = useState<boolean>(!!id);
    const isEditMode = !!id;

    const {
        register,
        handleSubmit,
        reset,
        formState: {errors, isDirty},
    } = useForm<VoiceTrainingScriptDTO>();

    useEffect(() => {
        const loadData = async () => {
            try {
                const orgs = await getAllOrganizations();
                setOrganizations(orgs.filter(org => org.organization_type === "agency"));

                if (isEditMode) {
                    setIsFetching(true);
                    setStatus(null);
                    const scriptData = await getScriptById(id!);
                    reset(scriptData);
                }
            } catch (err: any) {
                setStatus({type: "error", message: `Failed to load data: ${err.message}`});
            } finally {
                setIsFetching(false);
            }
        };

        loadData();
    }, [id]);

    const onSubmit: SubmitHandler<VoiceTrainingScriptDTO> = async (formData) => {
        setIsLoading(true);
        setStatus(null);

        try {
            // Clean up empty strings
            const cleanedData = {
                ...formData,
                industry_type: formData.industry_type || undefined,
                organization_id: formData.organization_id || undefined,
            };

            let result: VoiceTrainingScriptDTO;
            if (isEditMode) {
                result = await updateScript(id!, cleanedData);
                setStatus({type: "success", message: "Script updated successfully."});
                reset(result);
            } else {
                result = await createScript(cleanedData);
                setStatus({type: "success", message: "Script created successfully."});
                navigate(`/voice-training-scripts/edit/${result.id}`, {replace: true});
            }
        } catch (error: any) {
            setStatus({type: "error", message: `Failed to save script: ${error.message}`});
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <AppContainer>
                <div className="p-6">Loading script details...</div>
            </AppContainer>
        );
    }

    return (
        <AppContainer>
            <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-2xl font-semibold">
                        {isEditMode ? "Edit Voice Training Script" : "Create Voice Training Script"}
                    </h1>
                    <button
                        onClick={() => navigate("/voice-training-scripts")}
                        className="px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-700"
                    >
                        Back to List
                    </button>
                </div>

                {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4"/>}

                <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded shadow border space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                                Title <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="title"
                                {...register("title", {required: "Title is required"})}
                                className={`block w-full p-2 border rounded shadow-sm text-sm ${
                                    errors.title ? "border-red-500" : "border-gray-300"
                                } focus:ring-blue-500 focus:border-blue-500`}
                                placeholder="Enter script title"
                            />
                            {errors.title?.message && (
                                <p className="text-red-500 text-xs mt-1">{String(errors.title.message)}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                                Language
                            </label>
                            <input
                                type="text"
                                id="language"
                                {...register("language")}
                                className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="en"
                            />
                            <p className="text-gray-500 text-xs mt-1">ISO 639-1 language code (e.g., en, es, fr)</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="industry_type" className="block text-sm font-medium text-gray-700 mb-1">
                                Industry Type
                            </label>
                            <select
                                id="industry_type"
                                {...register("industry_type")}
                                className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">— No Industry (Public) —</option>
                                {INDUSTRY_TYPES.map((type) => (
                                    <option key={type.value} value={type.value}>
                                        {type.label}
                                    </option>
                                ))}
                            </select>
                            <p className="text-gray-500 text-xs mt-1">
                                Assign to a specific industry for targeted scripts
                            </p>
                        </div>

                        <div>
                            <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-1">
                                Organization
                            </label>
                            <select
                                id="organization_id"
                                {...register("organization_id")}
                                className="block w-full p-2 border rounded shadow-sm text-sm border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">— No Organization (Public) —</option>
                                {organizations.map((org) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-gray-500 text-xs mt-1">
                                Assign to a specific organization for private scripts
                            </p>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                            Script Content <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="content"
                            {...register("content", {required: "Content is required"})}
                            className={`block w-full p-2 border rounded shadow-sm text-sm ${
                                errors.content ? "border-red-500" : "border-gray-300"
                            } focus:ring-blue-500 focus:border-blue-500`}
                            placeholder="Enter the voice training script content..."
                            rows={12}
                        />
                        {errors.content?.message && (
                            <p className="text-red-500 text-xs mt-1">{String(errors.content.message)}</p>
                        )}
                        <p className="text-gray-500 text-xs mt-1">
                            The script text that will be used for voice training
                        </p>
                    </div>

                    <div className="flex justify-end pt-4 border-t">
                        <button
                            type="submit"
                            disabled={isLoading || !isDirty}
                            className="px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
                        >
                            {isLoading ? "Saving..." : isEditMode ? "Save Changes" : "Create Script"}
                        </button>
                    </div>
                </form>
            </div>
        </AppContainer>
    );
};

export default VoiceTrainingScriptEdit;

